import { useCallback, useEffect, useRef, useState } from 'react'

import Axios from '../networking/intraceptor'
import * as path from '../networking/urlEndPoints'

/**
 * Asks the backend who fulfils packaging orders, so screens never hard-code a hub
 * pharmacy id (PacMed used to hard-code 1502 / Weldon, Blister 865 / Prosper).
 *
 * Returns, for the logged-in pharmacy:
 *  - isPacmedHub / isBlisterHub   : it IS that module's hub
 *  - pacmedHubName / blisterHubName : the hub pharmacy's display name, so the
 *    one-time prompt can name it instead of saying "the hub pharmacy". Null when
 *    the backend cannot resolve it; callers fall back to generic wording.
 *  - canFulfilPacmed / canFulfilBlister : it packs that module's orders at all
 *    (because it is the hub, or because its self-fulfil switch is on)
 *  - pacmed / blister             : { needs_choice, mode } — whether this pharmacy
 *    still has to pick hub fill vs self fill for that module, and what it picked.
 *    The BACKEND decides needs_choice; the screens never compute it.
 *
 * Per-ORDER permission still comes from the order payload's `can_fulfil`.
 */
const useFulfilmentConfig = () => {
    const [config, setConfig] = useState(null)
    const [loading, setLoading] = useState(true)
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // On failure we deliberately fall back to a null config: every screen then
    // behaves exactly as it did before this hook existed, and the one-time
    // fulfilment prompt stays hidden rather than blocking a broken page.
    const fetchConfig = useCallback(() => {
        if (mountedRef.current) setLoading(true)
        return Axios.get(`${path.packaging_fulfilment_config}`)
            .then((response) => {
                const data = response?.data?.data ?? null
                if (mountedRef.current) setConfig(data)
                return data
            })
            .catch(() => {
                if (mountedRef.current) setConfig(null)
                return null
            })
            .finally(() => {
                if (mountedRef.current) setLoading(false)
            })
    }, [])

    useEffect(() => { fetchConfig() }, [fetchConfig])

    /**
     * Records the one-time hub-fill / self-fill choice for a module. Resolves with
     * the refreshed config so the page reflects the new mode straight away, and
     * rejects with the API error so the caller can surface it. A rejection also
     * re-reads the config (a 422 means someone already chose) so we never sit on a
     * stale prompt.
     */
    const submitFulfilmentChoice = useCallback((module, mode) => {
        return Axios.post(`${path.packaging_fulfilment_choice}`, { module, mode })
            .then((response) => {
                const data = response?.data?.data ?? null
                if (mountedRef.current) setConfig(data)
                return data
            })
            .catch((error) => {
                return fetchConfig().then(() => { throw error })
            })
    }, [fetchConfig])

    return {
        config,
        loading,
        isPacmedHub: config?.is_pacmed_hub ?? false,
        isBlisterHub: config?.is_blister_hub ?? false,
        pacmedHubName: config?.pacmed_hub_pharmacy_name ?? null,
        blisterHubName: config?.blister_hub_pharmacy_name ?? null,
        canFulfilPacmed: config?.can_fulfil_pacmed ?? false,
        canFulfilBlister: config?.can_fulfil_blister ?? false,
        pacmedNeedsChoice: config?.pacmed?.needs_choice ?? false,
        pacmedMode: config?.pacmed?.mode ?? null,
        blisterNeedsChoice: config?.blister?.needs_choice ?? false,
        blisterMode: config?.blister?.mode ?? null,
        refetchConfig: fetchConfig,
        submitFulfilmentChoice,
    }
}

export default useFulfilmentConfig
